import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Tag, Button } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined, EyeOutlined } from '@ant-design/icons';
import AppDetailsModal from './AppDetailsModal.jsx';

const OverallSummary = ({ summary, totalApps, appsNeedingReview, isNewLogic, sessionId, projectId, sessionData }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState('');

  const handleStatusClick = (statusKey, title) => {
    setSelectedStatus(statusKey);
    setSelectedTitle(title);
    setModalVisible(true);
  };
  const summaryCards = [
    {
      key: 'pass',
      title: 'Pass',
      value: summary.pass,
      rate: summary.pass_rate,
      color: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      borderColor: 'rgba(34, 197, 94, 0.3)',
      icon: <CheckCircleOutlined />,
      description: 'Low scores + No compliance issues'
    },
    {
      key: 'refer',
      title: 'Refer',
      value: summary.refer,
      rate: summary.refer_rate,
      color: '#f97316',
      backgroundColor: 'rgba(249, 115, 22, 0.1)',
      borderColor: 'rgba(249, 115, 22, 0.3)',
      icon: <ExclamationCircleOutlined />,
      description: 'Borderline scores + No compliance issues'
    },
    {
      key: 'fail',
      title: 'Fail',
      value: summary.fail,
      rate: summary.fail_rate,
      color: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: 'rgba(239, 68, 68, 0.3)',
      icon: <CloseCircleOutlined />,
      description: 'Higher scores + Compliance issues'
    },
    {
      key: 'other',
      title: 'Other',
      value: summary.other,
      rate: totalApps > 0 ? ((summary.other / totalApps) * 100) : 0,
      color: '#94a3b8',
      backgroundColor: 'rgba(148, 163, 184, 0.1)',
      borderColor: 'rgba(148, 163, 184, 0.3)',
      icon: <QuestionCircleOutlined />,
      description: 'Mixed patterns requiring individual review'
    }
  ];

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: '16px' 
      }}>
        <h3 style={{ color: '#ffffff', margin: 0, fontWeight: '600', fontSize: '16px' }}>
          Overall Analysis Results
        </h3>
        
      </div>
      
      <Row gutter={[16, 16]}>
        {summaryCards.map((card) => (
          <Col span={6} key={card.key}>
            <Card 
              size="small"
              style={{ 
                backgroundColor: card.backgroundColor,
                border: `1px solid ${card.borderColor}`,
                borderRadius: '8px'
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '24px', 
                  color: card.color, 
                  marginBottom: '8px' 
                }}>
                  {card.icon}
                </div>
                <Statistic
                  title={
                    <span style={{ 
                      color: '#e2e8f0', 
                      fontSize: '13px', 
                      fontWeight: '500' 
                    }}>
                      {card.title}
                    </span>
                  }
                  value={card.value}
                  valueStyle={{ 
                    color: card.color, 
                    fontSize: '24px', 
                    fontWeight: '700',
                    marginBottom: '4px'
                  }}
                />
                <div style={{ 
                  color: card.color, 
                  fontSize: '14px', 
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  {card.rate.toFixed(1)}%
                </div>
                <div style={{ 
                  color: '#94a3b8', 
                  fontSize: '11px', 
                  lineHeight: '1.3',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  marginBottom: '8px'
                }}>
                  {card.description}
                </div>
                {card.value > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handleStatusClick(card.key, card.title)}
                    style={{ 
                      color: card.color,
                      fontSize: '11px',
                      height: '24px',
                      padding: '0 8px'
                    }}
                  >
                    View Apps
                  </Button>
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card 
        size="small"
        style={{ 
          marginTop: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Row gutter={16} style={{ textAlign: 'center' }}>
          <Col span={12}>
            <Statistic
              title={
                <span style={{ 
                  color: '#e2e8f0', 
                  fontSize: '13px', 
                  fontWeight: '500' 
                }}>
                  Apps Needing Review
                </span>
              }
              value={appsNeedingReview}
              valueStyle={{ 
                color: '#f97316', 
                fontSize: '20px', 
                fontWeight: '700' 
              }}
              suffix={
                <span style={{ 
                  color: '#94a3b8', 
                  fontSize: '14px',
                  fontWeight: '400'
                }}>
                  ({((appsNeedingReview / totalApps) * 100).toFixed(1)}%)
                </span>
              }
            />
            <div style={{ 
              color: '#94a3b8', 
              fontSize: '12px', 
              marginTop: '4px' 
            }}>
              {summary.refer} refer + {summary.fail} fail
            </div>
          </Col>
          <Col span={12}>
            <Statistic
              title={
                <span style={{ 
                  color: '#e2e8f0', 
                  fontSize: '13px', 
                  fontWeight: '500' 
                }}>
                  Total Evaluated
                </span>
              }
              value={summary.total_evaluated}
              valueStyle={{ 
                color: '#ffffff', 
                fontSize: '20px', 
                fontWeight: '700' 
              }}
              suffix={
                <span style={{ 
                  color: '#94a3b8', 
                  fontSize: '14px',
                  fontWeight: '400'
                }}>
                  of {totalApps}
                </span>
              }
            />
            {summary.errors_or_missing > 0 && (
              <div style={{ 
                color: '#fca5a5', 
                fontSize: '12px', 
                marginTop: '4px' 
              }}>
                {summary.errors_or_missing} with incomplete data
              </div>
            )}
          </Col>
        </Row>
      </Card>

      

      <AppDetailsModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        sessionId={sessionId}
        projectId={projectId}
        statusType={selectedStatus}
        statusTitle={selectedTitle}
        initialData={sessionData?.results_by_type?.[selectedStatus]}
      />
    </div>
  );
};

export default OverallSummary;